import React, { useState, useMemo, useRef, useEffect } from 'react';

/**
 * TRANSFORMATION READINESS ASSESSMENT v2.7
 * 
 * Based on Adam Libman's framework combining:
 * - The Transformation Equation: Shattered + Desire + Willingness > Ego + Fear + Results
 * - Alex Hormozi's insight on identity-level constraints
 * - The Jewish Wealth Cycle: Receive → Contain → Give
 * 
 * v2.7 Changes:
 * - Tightened will3 subtext to explicitly test "you're the problem" feedback
 * - Added derived coachability flag (will3 + proof3 combo):
 *   - Red: won't change mind AND blames others = do not sell full advisory
 *   - Yellow: resistant OR blame tendency = address early
 * 
 * Scoring Architecture:
 * - Overall %: 27 questions across 6 sections (54 points max)
 * - Hard Gates: CAPACITY <50%, PROOF <50% → forces Conditional
 * - Identity Score: 10.5 points max → ≥2.0 triggers Identity track
 * - Fit Flags: Red (blame, no authority, low coachability) / Yellow (various warnings)
 * 
 * Question breakdown:
 * - CAPACITY: 4 (cap1-3, som2)
 * - WILLINGNESS: 7 (will1-6, som1)
 * - DESIRE: 4 (des1-4) — all raw intensity based
 * - RESOURCES: 3 (res1-3)
 * - TIMING: 5 (time1-5)
 * - BEHAVIOR PROOF: 4 (proof1-4)
 * - TOTAL: 27 questions
 */

const TransformationReadinessAssessment = () => {
  const [currentSection, setCurrentSection] = useState(0);
  const [started, setStarted] = useState(false);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [email, setEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(false);
  
  // Tracking: generate stable session ID
  const sessionIdRef = useRef(null);
  if (!sessionIdRef.current) {
    sessionIdRef.current = crypto.randomUUID();
  }

  // ============================================================================
  // ASSESSMENT QUESTIONS (v2.0 - Behavioral & Time-Bound)
  // ============================================================================
  
  const sections = [
    {
      id: 'capacity',
      title: 'CAPACITY',
      subtitle: 'Do you have the bandwidth to implement change?',
      color: '#3b82f6',
      icon: '⚡',
      questions: [
        {
          id: 'cap1',
          text: 'In the last 4 weeks, have you consistently had 5+ hours per week for strategic work (not firefighting)?',
          subtext: 'Think about actual calendar time spent working ON the business, not IN it.',
        },
        {
          id: 'cap2',
          text: 'In the last 90 days, have you had at least one continuous 4-6 week stretch where you weren\'t in crisis mode?',
          subtext: 'A stretch where you could focus on improvement rather than firefighting.',
        },
        {
          id: 'cap3',
          text: 'Can your business operate for 1 full day without you touching email, Slack, or making decisions?',
          subtext: 'This tests whether you have team or systems that can handle operations.',
        },
        {
          id: 'som2',
          text: 'When you think about delegating a core task you currently do (sales calls, key client work, cash decisions) to a competent team member, what happens in your body?',
          subtext: 'Notice your physical response — relief, tension, or resistance.',
          options: [
            { value: 2, label: 'Mostly okay — I\'d watch it, but I can imagine letting go' },
            { value: 1, label: 'Noticeable tension — worry or urge to double-check, but I could try' },
            { value: 0, label: 'Body says "absolutely not" — anxiety, tightness, urge to take it back' }
          ]
        },
      ]
    },
    {
      id: 'willingness',
      title: 'WILLINGNESS',
      subtitle: 'Have you actually done uncomfortable things?',
      color: '#22c55e',
      icon: '🔓',
      questions: [
        {
          id: 'will1',
          text: 'In the last 12 months, have you raised prices or eliminated discounts, even though it felt uncomfortable?',
          subtext: 'Not planned to. Actually did it.',
        },
        {
          id: 'will2',
          text: 'In the last 12 months, have you fired or stopped working with at least one client who drained you, even though they were paying?',
          subtext: 'Enforced a standard or boundary that cost you money short-term.',
        },
        {
          id: 'will3',
          text: 'In the last 6 months, have you changed your mind on a major decision because someone you respect convinced you that you were wrong?',
          subtext: 'Actually changed your behavior after someone told you your leadership or decisions were the main problem — not just "considered" their input.',
        },
        {
          id: 'will4',
          text: 'In the last 6 months, did you maintain one boring system (KPI review, budget review, outreach cadence) consistently for 8+ weeks?',
          subtext: 'Consistency on something that doesn\'t give immediate dopamine.',
        },
        {
          id: 'will5',
          text: 'If, after we diagnose your business together, we both agree you need to let someone go — including a key salesperson or long-time team member — and that will likely reduce revenue or profit for 1–3 months, are you willing to make that change?',
          subtext: 'Assume we\'ve run the numbers and it clearly improves the model over the next 12–24 months.',
        },
        {
          id: 'will6',
          text: 'In the last 60 days, did you make at least one meaningful decision with incomplete information (pricing, hiring, offer, positioning) instead of waiting for certainty?',
          subtext: 'This measures your ability to act under uncertainty — analysis paralysis kills transformation.',
        },
        {
          id: 'som1',
          text: 'When you imagine quoting a price 2x what you currently charge to a good-fit prospect, what happens in your body?',
          subtext: 'Notice your physical response, not your logical justification.',
          options: [
            { value: 2, label: 'Mostly calm — some butterflies, but I can breathe and speak clearly' },
            { value: 1, label: 'Noticeable tension — tight chest or shallow breath, but I could push through' },
            { value: 0, label: 'Panic or shutdown — tight throat, can\'t imagine actually saying it' }
          ]
        },
      ]
    },
    {
      id: 'desire',
      title: 'DESIRE',
      subtitle: 'How badly do you actually want this?',
      color: '#f59e0b',
      icon: '🔥',
      questions: [
        {
          id: 'des1',
          text: 'How often do you think about the business and life you want — not just when things are broken, but when things are fine?',
          subtext: 'Real desire doesn\'t wait for pain. It nags you even when things are "good enough."',
          options: [
            { value: 2, label: 'Constantly — it\'s background noise in my head, I can\'t turn it off' },
            { value: 1, label: 'Regularly — a few times a week, especially when something triggers it' },
            { value: 0, label: 'Occasionally — mostly when I\'m frustrated or someone brings it up' }
          ]
        },
        {
          id: 'des2',
          text: 'When you imagine your business and life being exactly the same 3 years from now — same revenue, same stress, same ceiling — what happens inside you?',
          subtext: 'This tests whether your desire is a "want" or a "must."',
          options: [
            { value: 2, label: 'Visceral rejection — something in me says "I would rather die than stay here"' },
            { value: 1, label: 'Discomfort — I don\'t love it, but I could survive' },
            { value: 0, label: 'Honestly fine — it\'s not ideal but I\'d adapt' }
          ]
        },
        {
          id: 'des3',
          text: 'If the only way to get the business and life you say you want meant nuking your current comfort — walking away from the "good" setup, moving into a crap apartment, sleeping on a mattress on the floor, cutting every non-essential expense, and having people quietly say "what happened to them?" for 12–18 months — would you still do it?',
          subtext: 'Not in theory. Worse car, worse zip code, no nice dinners, no ego buys, more hours for less money. Do you want it badly enough to live like that on purpose?',
          options: [
            { value: 2, label: 'Yes — I\'d burn it all down if that\'s what it took' },
            { value: 1, label: 'Maybe — depends on the specifics' },
            { value: 0, label: 'No — some things aren\'t worth sacrificing' }
          ]
        },
        {
          id: 'des4',
          text: 'Be brutally honest: Is your desire for breakthrough a "nice to have" or a "I literally cannot keep living like this"?',
          subtext: '"Nice to have" doesn\'t survive the first hard quarter. "Cannot live like this" does.',
          options: [
            { value: 2, label: 'Cannot keep living like this — the current situation is killing me spiritually, emotionally, or physically' },
            { value: 1, label: 'Strong want — I really want change but I\'m not desperate' },
            { value: 0, label: 'Nice to have — I\'d like improvement but I\'m fundamentally okay where I am' }
          ]
        },
      ]
    },
    {
      id: 'resources',
      title: 'RESOURCES',
      subtitle: 'Do you have what\'s needed to execute?',
      color: '#8b5cf6',
      icon: '💎',
      questions: [
        {
          id: 'res1',
          text: 'Can you fund a 90-day change initiative without risking payroll, taxes, or your personal financial stability?',
          subtext: 'This could be cash reserves, reliable credit, or stable recurring margin.',
        },
        {
          id: 'res2',
          text: 'Do you have access to the skills needed to implement changes—either yourself, your team, or through contractors you can afford?',
          subtext: 'Or will skill gaps block execution?',
        },
        {
          id: 'res3',
          text: 'Do you have a support system (spouse, mentor, peer group, coach) who will encourage change rather than resist it?',
          subtext: 'Environmental resistance kills more transformations than lack of knowledge.',
        },
      ]
    },
    {
      id: 'timing',
      title: 'TIMING',
      subtitle: 'Is now the right moment for transformation?',
      color: '#ec4899',
      icon: '⏰',
      questions: [
        {
          id: 'time1',
          text: 'Are you at an inflection point where the cost of staying the same clearly exceeds the cost and discomfort of change?',
          subtext: 'The equation: your pain + desire must exceed your ego + fear + attachment to current results.',
        },
        {
          id: 'time2',
          text: 'Are major life disruptions (divorce, health crisis, family emergency, legal issues) NOT consuming your attention right now?',
          subtext: 'Sometimes the right move is to stabilize life first.',
        },
        {
          id: 'time3',
          text: 'Right now, are you out of survival mode (payroll and taxes not in immediate danger) and able to step back weekly to work ON the business?',
          subtext: 'Survival mode prevents strategic thinking.',
        },
        {
          id: 'time4',
          text: 'Have you invested in courses, coaching, or tools in the past 2 years and still not implemented what you learned?',
          subtext: 'This pattern suggests the constraint may be internal, not informational.',
          reversed: true, // Yes = bad
        },
        {
          id: 'time5',
          text: 'Can you clearly articulate what you want your business and life to look like in 3 years—in 3-5 specific sentences?',
          subtext: 'Example: "I want $2M revenue, 3 employees, 30-hour weeks, and 8 weeks vacation." Clarity is required for transformation.',
        },
      ]
    },
    {
      id: 'proof',
      title: 'BEHAVIOR PROOF',
      subtitle: 'Do your actions match your intentions?',
      color: '#06b6d4',
      icon: '🎯',
      questions: [
        {
          id: 'proof1',
          text: 'In the last 30 days, did you execute a meaningful change you didn\'t feel like doing (raised price, had hard conversation, shipped something, delegated)?',
          subtext: 'Recent action is the best predictor of future action.',
        },
        {
          id: 'proof2',
          text: 'When you set a weekly commitment to yourself, do you complete it at least 3 out of 4 weeks?',
          subtext: 'This measures follow-through consistency, not just intention.',
        },
        {
          id: 'proof3',
          text: 'In the last 12 months, have you hired a coach, agency, or vendor and later concluded the outcome was 100% their fault (with zero changes needed on your side)?',
          subtext: 'Be honest—this reveals blame patterns that derail engagements.',
          reversed: true, // Yes = bad, No = good
        },
        {
          id: 'proof4',
          text: 'Can you make and implement changes (pricing, offers, hiring, budget) without needing approval from a spouse, partner, or board who is likely to veto or stall them?',
          subtext: 'Decision authority is critical for execution speed.',
        },
      ]
    },
  ];

  // ============================================================================
  // TRACKING (v1 - Simple Gauges)
  // ============================================================================
  
  const trackEvent = (eventName, payload = {}) => {
    const eventData = {
      event: eventName,
      sessionId: sessionIdRef.current,
      timestamp: new Date().toISOString(),
      ...payload
    };
    
    // Log to console (visible in browser dev tools)
    console.log(`[TRA Track] ${eventName}`, eventData);
    
    // OPTION A: Send to your backend (uncomment when ready)
    // fetch('/api/track', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   keepalive: true,
    //   body: JSON.stringify(eventData)
    // }).catch(() => {});
    
    // OPTION B: Send to PostHog/GA (uncomment if using)
    // window.posthog?.capture(eventName, eventData);
    // window.gtag?.('event', eventName, eventData);
  };

  // Track abandonment when user leaves page
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (started && !showResults && Object.keys(answers).length > 0) {
        trackEvent('tra_abandon', {
          lastSectionId: sections[currentSection]?.id,
          lastSectionIndex: currentSection,
          answeredCount: Object.keys(answers).length,
          totalQuestions: sections.reduce((sum, s) => sum + s.questions.length, 0)
        });
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [started, showResults, answers, currentSection]);

  // ============================================================================
  // SCORING LOGIC (v2.0 - with Capacity Gating)
  // ============================================================================

  const calculateResults = useMemo(() => {
    if (Object.keys(answers).length === 0) return null;

    // Calculate section scores, handling reversed questions
    const sectionScores = sections.map(section => {
      const sectionAnswers = section.questions.map(q => {
        const answer = answers[q.id] || 0;
        // For reversed questions (Yes=bad), flip the score: 0->2, 1->1, 2->0
        if (q.reversed) {
          return 2 - answer;
        }
        return answer;
      });
      const maxScore = section.questions.length * 2;
      const actualScore = sectionAnswers.reduce((sum, a) => sum + a, 0);
      const percentage = Math.round((actualScore / maxScore) * 100);
      
      return {
        id: section.id,
        title: section.title,
        color: section.color,
        score: actualScore,
        maxScore,
        percentage,
        status: percentage >= 75 ? 'strong' : percentage >= 50 ? 'moderate' : 'weak'
      };
    });

    const totalScore = sectionScores.reduce((sum, s) => sum + s.score, 0);
    const maxTotal = sectionScores.reduce((sum, s) => sum + s.maxScore, 0);
    const overallPercentage = Math.round((totalScore / maxTotal) * 100);

    // ==========================================================================
    // FIT FLAGS - "Think Twice" indicators
    // ==========================================================================
    
    const fitFlags = [];
    
    // Blame pattern (proof3)
    // NOTE: proof3 is reversed for section %, but we use RAW answer here:
    // Raw 2 = "Yes, I blamed them 100%" → red flag
    // Raw 0 = "No, I took responsibility" → no flag
    if (answers.proof3 === 2) {
      fitFlags.push({
        type: 'blame_pattern',
        severity: 'red',
        message: 'Blame pattern detected — history of attributing failures 100% to others'
      });
    } else if (answers.proof3 === 1) {
      fitFlags.push({
        type: 'blame_pattern',
        severity: 'yellow',
        message: 'Possible blame tendency — may struggle with accountability'
      });
    }
    
    // Decision authority (proof4) - No (0) = red flag
    if (answers.proof4 === 0) {
      fitFlags.push({
        type: 'decision_authority',
        severity: 'red',
        message: 'No decision authority — changes require approval from others who may veto'
      });
    } else if (answers.proof4 === 1) {
      fitFlags.push({
        type: 'decision_authority',
        severity: 'yellow',
        message: 'Limited decision authority — some friction expected on implementation'
      });
    }
    
    const hasRedFlags = fitFlags.some(f => f.severity === 'red');

    // Find the constraint (weakest dimension)
    let constraint = sectionScores.reduce((min, s) => 
      s.percentage < min.percentage ? s : min, sectionScores[0]);

    // ==========================================================================
    // HARD GATES (Non-Negotiables) - These override overall percentage
    // ==========================================================================
    
    // Gate A: CAPACITY - Must have bandwidth
    const capacitySection = sectionScores.find(s => s.id === 'capacity');
    const capacityGated = capacitySection && capacitySection.percentage < 50;
    
    // Gate B: BEHAVIOR PROOF - Must have recent action
    const proofSection = sectionScores.find(s => s.id === 'proof');
    const proofGated = proofSection && proofSection.percentage < 50;
    
    // ==========================================================================
    // DESIRE FIT FLAG (Not a gate - yellow warning only)
    // Someone with moderate desire can still be foundation/scoped client,
    // they just shouldn't be sold a 10x story
    // ==========================================================================
    
    const desireSection = sectionScores.find(s => s.id === 'desire');
    if (desireSection && desireSection.percentage < 50) {
      fitFlags.push({
        type: 'low_desire',
        severity: 'yellow',
        message: 'Desire may be intellectual, not visceral — transformation requires "must have" not "nice to have"'
      });
    }

    // CONSTRAINT OVERRIDE: If gated, force constraint to be the gated section
    if (capacityGated) {
      constraint = capacitySection;
    } else if (proofGated) {
      constraint = proofSection;
    }

    // ==========================================================================
    // DETERMINE READINESS LEVEL
    // Thresholds: Ready 80%+, Conditional 60-79%, Not Ready <60%
    // Gates override overall% when tripped
    // ==========================================================================
    
    let readinessLevel, readinessMessage, recommendation;
    
    // SIMPLIFIED GATE LOGIC: If gated, Not Ready (work on yourself first)
    if (capacityGated) {
      readinessLevel = 'notReady';
      readinessMessage = 'STABILIZE FIRST';
      recommendation = 'You don\'t have the bandwidth for transformation right now. No engagement, coach, or system will help until you create space. The work is creating margin in your life first—clear the chaos before attempting strategic change.';
    } else if (proofGated) {
      readinessLevel = 'notReady';
      readinessMessage = 'INTENTION-ACTION GAP';
      recommendation = 'Your intentions don\'t match your actions. This isn\'t about strategy or tactics—it\'s about you. Until you close the gap between what you say you\'ll do and what you actually do, no advisor, coach, or system will help. The work right now is internal.';
    } else if (overallPercentage >= 80) {
      // Passed all gates + high overall = Ready
      readinessLevel = 'ready';
      readinessMessage = 'TRANSFORMATION READY';
      recommendation = 'You have the conditions in place for meaningful change. Your foundation is solid—capacity, willingness, and follow-through are aligned. Let\'s get to work.';
    } else if (overallPercentage >= 60) {
      // Passed gates but moderate overall = Conditional
      readinessLevel = 'conditional';
      readinessMessage = 'CONDITIONALLY READY';
      recommendation = `You have potential, but ${constraint.title} is your constraint. Address this dimension first, or transformation efforts will stall.`;
    } else {
      // Low overall = Not Ready
      readinessLevel = 'notReady';
      readinessMessage = 'NOT YET READY';
      recommendation = 'The conditions for transformation aren\'t in place yet. This isn\'t judgment—it\'s information. Forcing change now will likely fail and waste resources. Focus on building foundation first.';
    }

    // Check for identity-level indicators using weighted scoring
    // No = 1 point, Somewhat = 0.5 points, Yes = 0 points
    // Flag as identity-constrained if total >= 2.0
    let identityScore = 0;
    const identityIndicators = [];
    
    // Pricing resistance (will1)
    if (answers.will1 === 0) {
      identityScore += 1;
      identityIndicators.push('Pricing resistance — haven\'t raised prices despite discomfort (Receive block)');
    } else if (answers.will1 === 1) {
      identityScore += 0.5;
      identityIndicators.push('Pricing hesitancy — inconsistent on raising prices (partial Receive block)');
    }
    
    // Ego protection (will3)
    if (answers.will3 === 0) {
      identityScore += 1;
      identityIndicators.push('Ego protection — difficulty changing mind when shown to be wrong');
    } else if (answers.will3 === 1) {
      identityScore += 0.5;
      identityIndicators.push('Ego sensitivity — occasionally changes mind, but inconsistently');
    }
    
    // ==========================================================================
    // DESIRE IDENTITY SCORING (Raw Intensity)
    // ==========================================================================
    
    // des1 - Obsession (thinks about it constantly)
    if (answers.des1 === 0) {
      identityScore += 0.5;
      identityIndicators.push('Low obsession — only thinks about change when triggered by pain');
    }
    
    // des2 - Intolerance of status quo
    if (answers.des2 === 0) {
      identityScore += 1;
      identityIndicators.push('Tolerates status quo — can accept staying stuck, desire is a "want" not a "must"');
    } else if (answers.des2 === 1) {
      identityScore += 0.5;
      identityIndicators.push('Moderate status quo tolerance — uncomfortable but survivable');
    }
    
    // des3 - Sacrifice willingness ("sleep on the floor")
    if (answers.des3 === 0) {
      identityScore += 1;
      identityIndicators.push('Avoids deep lifestyle and status sacrifice — desire may be intellectual, not visceral');
    } else if (answers.des3 === 1) {
      identityScore += 0.5;
      identityIndicators.push('Partial willingness to downgrade lifestyle — may pull back when sacrifices get painful');
    }
    
    // des4 - Emotional charge (need vs nice-to-have)
    if (answers.des4 === 0) {
      identityScore += 1;
      identityIndicators.push('Desire is "nice to have" — won\'t survive the first hard quarter');
    } else if (answers.des4 === 1) {
      identityScore += 0.5;
      identityIndicators.push('Strong want but not desperate — may waver when cost becomes real');
    }
    
    // ==========================================================================
    // OTHER IDENTITY INDICATORS
    // ==========================================================================
    
    // Chronic implementation gap (time4) - ONLY count if NOT capacity gated
    // If they're capacity gated, non-implementation might just be "no oxygen", not identity
    // NOTE: time4 is reversed for section %, but we use RAW answer here:
    // Raw 2 = "Yes, I invested and didn't implement" → identity hit
    // Raw 0 = "No, I implemented what I learned" → no identity hit
    if (!capacityGated) {
      if (answers.time4 === 2) {
        identityScore += 1;
        identityIndicators.push('Pattern of investing but not implementing — constraint is internal, not informational');
      } else if (answers.time4 === 1) {
        identityScore += 0.5;
        identityIndicators.push('Partial implementation gap — some investments didn\'t stick');
      }
    }
    
    // Action gap (proof1 + proof2)
    const proofAvg = ((answers.proof1 || 0) + (answers.proof2 || 0)) / 2;
    if (proofAvg <= 0.5) {
      identityScore += 1;
      identityIndicators.push('Action gap — intentions consistently don\'t match behaviors');
    } else if (proofAvg <= 1) {
      identityScore += 0.5;
      identityIndicators.push('Inconsistent follow-through — actions sometimes match intentions');
    }
    
    // SOMATIC: Pricing body response (som1) - Receive block
    if (answers.som1 === 0) {
      identityScore += 1;
      identityIndicators.push('Body panic around higher pricing — nervous system rejects Receive');
    } else if (answers.som1 === 1) {
      identityScore += 0.5;
      identityIndicators.push('Body tension around higher pricing — Receive work needed');
    }
    
    // SOMATIC: Delegation body response (som2) - Containment/control block
    if (answers.som2 === 0) {
      identityScore += 1;
      identityIndicators.push('Control pattern — body cannot tolerate true delegation (Containment block)');
    } else if (answers.som2 === 1) {
      identityScore += 0.5;
      identityIndicators.push('Delegation tension — likely to sabotage or re-take tasks');
    }
    
    // Valley-of-death willingness (will5)
    if (answers.will5 === 0) {
      identityScore += 1;
      identityIndicators.push('Avoids planned short-term dips — reluctant to let go of key people even when the model demands it');
    } else if (answers.will5 === 1) {
      identityScore += 0.5;
      identityIndicators.push('Limited tolerance for short-term dips — will need strong conviction to make structural people changes');
    }
    
    // COMBO FIT FLAG: Founder bottleneck + strong control pattern
    if (answers.cap3 === 0 && answers.som2 === 0) {
      fitFlags.push({
        type: 'founder_bottleneck',
        severity: 'yellow',
        message: 'Founder bottleneck + strong control pattern — delegation work required before meaningful CFO engagement'
      });
    }
    
    // FIT FLAG: People change intolerance
    if (answers.will5 === 0) {
      fitFlags.push({
        type: 'people_change_intolerance',
        severity: 'yellow',
        message: 'Low tolerance for firing key/long-time team members even when diagnostics support it — must align on willingness to make structural people changes'
      });
    }
    
    // ==========================================================================
    // DERIVED COACHABILITY FLAG (will3 + proof3 combo)
    // will3 = ego flexibility (changed mind when shown wrong)
    // proof3 = blame pattern (blamed others 100%)
    // ==========================================================================
    
    // Red coachability: won't change mind AND blames others completely
    if (answers.will3 === 0 && answers.proof3 === 2) {
      fitFlags.push({
        type: 'low_coachability',
        severity: 'red',
        message: 'Low coachability pattern: avoids responsibility and resists feedback — do not sell full advisory'
      });
    }
    // Yellow coachability: either resistant to feedback OR has blame tendency
    else if ((answers.will3 === 0 || answers.will3 === 1) && (answers.proof3 === 1 || answers.proof3 === 2)) {
      fitFlags.push({
        type: 'coachability_concern',
        severity: 'yellow',
        message: 'May struggle with direct feedback that points at their own behavior — address early in engagement'
      });
    }
    
    const isLikelyIdentityConstrained = identityScore >= 2.0;

    // Determine recommended track
    // - TACTICAL: Ready + passed gates + < 2 identity flags
    // - IDENTITY: Any score + 2+ identity flags (inner work first)
    // - FOUNDATION: Conditional/NotReady + < 2 identity flags (stabilize)
    let recommendedTrack;
    if (isLikelyIdentityConstrained) {
      recommendedTrack = 'identity';
    } else if (readinessLevel === 'ready') {
      recommendedTrack = 'tactical';
    } else {
      recommendedTrack = 'foundation';
    }

    return {
      sectionScores,
      totalScore,
      maxTotal,
      overallPercentage,
      constraint,
      capacityGated,
      proofGated,
      readinessLevel,
      readinessMessage,
      recommendation,
      identityIndicators,
      identityScore,
      isLikelyIdentityConstrained,
      recommendedTrack,
      fitFlags,
      hasRedFlags
    };
  }, [answers]);

  // ============================================================================
  // HELPER FUNCTIONS (must be after calculateResults)
  // ============================================================================

  // Build Calendly URL with assessment results as params
  const buildCalendlyUrl = (baseUrl) => {
    const results = calculateResults;
    if (!results) return baseUrl;
    
    const params = new URLSearchParams({
      a1: results.overallPercentage?.toString() || '',           // Overall %
      a2: results.readinessLevel || '',                          // ready/conditional/notReady
      a3: results.recommendedTrack || '',                        // tactical/identity/foundation  
      a4: results.constraint?.title || '',                       // Primary constraint
      a5: results.identityScore?.toFixed(1) || '',              // Identity score
      a6: results.hasRedFlags ? 'red_flags' : ''                // Fit flags indicator
    });
    
    return `${baseUrl}?${params.toString()}`;
  };

  // Track CTA clicks
  const handleCtaClick = (ctaType, url) => {
    const results = calculateResults;
    trackEvent('tra_cta_click', {
      ctaType,
      readinessLevel: results?.readinessLevel,
      recommendedTrack: results?.recommendedTrack
    });
    window.open(url, '_blank');
  };

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleNext = () => {
    if (currentSection < sections.length - 1) {
      // Track section completion
      trackEvent('tra_section_complete', { 
        sectionId: sections[currentSection].id, 
        sectionIndex: currentSection,
        sectionTitle: sections[currentSection].title
      });
      setCurrentSection(prev => prev + 1);
      window.scrollTo(0, 0);
    } else {
      // Track assessment completion with results
      const results = calculateResults;
      trackEvent('tra_complete', {
        overallPercentage: results?.overallPercentage,
        readinessLevel: results?.readinessLevel,
        recommendedTrack: results?.recommendedTrack,
        constraint: results?.constraint?.title,
        identityScore: results?.identityScore,
        capacityGated: results?.capacityGated,
        proofGated: results?.proofGated
      });
      setShowResults(true);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentSection > 0) {
      setCurrentSection(prev => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  const resetAssessment = () => {
    setStarted(false);
    setCurrentSection(0);
    setAnswers({});
    setShowResults(false);
    setEmail('');
    setEmailSubmitted(false);
    setShowWaitlist(false);
    window.scrollTo(0, 0);
  };

  const handleEmailSubmit = () => {
    if (!email) return;
    
    const results = calculateResults;
    if (!results) return;
    
    // Track email submission
    trackEvent('tra_email_submit', {
      readinessLevel: results.readinessLevel,
      recommendedTrack: results.recommendedTrack,
      overallPercentage: results.overallPercentage
    });
    
    // Build the results summary for email body
    const sectionSummary = results.sectionScores
      .map(s => `• ${s.title}: ${s.percentage}%`)
      .join('\n');
    
    const trackNames = {
      tactical: 'Tactical Path',
      identity: 'Identity/Foundation Path',
      foundation: 'Stabilization Path'
    };
    
    const levelNames = {
      ready: 'TRANSFORMATION READY',
      conditional: 'CONDITIONALLY READY',
      notReady: 'NOT YET READY'
    };
    
    const emailBody = `Hi Adam,

Here are my Transformation Readiness Assessment results:

OVERALL: ${results.overallPercentage}% - ${levelNames[results.readinessLevel]}
RECOMMENDED TRACK: ${trackNames[results.recommendedTrack]}

SECTION SCORES:
${sectionSummary}

PRIMARY CONSTRAINT: ${results.constraint.title}
${results.isLikelyIdentityConstrained ? `IDENTITY SCORE: ${results.identityScore.toFixed(1)}/5 (Identity-level constraint likely)` : ''}
${results.fitFlags.length > 0 ? `\nFIT FLAGS: ${results.fitFlags.map(f => f.message).join('; ')}` : ''}

I'd like to discuss next steps.

---
Sent from Transformation Readiness Assessment
`.trim();

    const subject = `My Transformation Readiness Results - ${results.overallPercentage}% ${levelNames[results.readinessLevel]}`;
    
    // Your email address - CHANGE THIS
    const yourEmail = 'adam@adamlibman.com';
    
    // Create mailto link
    const mailtoLink = `mailto:${yourEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(emailBody)}`;
    
    // Open email client
    window.open(mailtoLink, '_blank');
    
    setEmailSubmitted(true);
  };

  const currentSectionData = sections[currentSection];
  const currentQuestions = currentSectionData?.questions || [];
  const allCurrentAnswered = currentQuestions.every(q => answers[q.id] !== undefined);

  // Total questions count
  const totalQuestions = sections.reduce((sum, s) => sum + s.questions.length, 0);

  // ============================================================================
  // RENDER: LANDING PAGE
  // ============================================================================

  if (!started) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
        padding: '40px 20px',
        fontFamily: "'Space Grotesk', -apple-system, sans-serif"
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔮</div>
            <h1 style={{
              fontSize: '2.5rem',
              fontWeight: '700',
              color: '#f8fafc',
              marginBottom: '16px',
              lineHeight: '1.2'
            }}>
              Transformation Readiness Assessment
            </h1>
            <p style={{
              fontSize: '1.1rem',
              color: '#94a3b8',
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              I've spent 25 years watching entrepreneurs stay stuck despite having all the required resources needed for breakthrough success. The difference between those who breakthrough and those who remain stuck isn't intelligence or tactics—it's whether your conditions are right for a breakthrough + your ability to implement change. Let's find out if you are ready.
            </p>
          </div>

          {/* The 6 Dimensions */}
          <div style={{
            background: 'rgba(255,255,255,0.03)',
            borderRadius: '16px',
            padding: '32px',
            marginBottom: '32px'
          }}>
            <h2 style={{ color: '#f8fafc', fontSize: '1.3rem', marginBottom: '24px' }}>
              We'll assess 6 dimensions of readiness:
            </h2>
            
            <div style={{ display: 'grid', gap: '16px' }}>
              {[
                { icon: '⚡', title: 'CAPACITY', desc: 'Do you have bandwidth to implement?', color: '#3b82f6' },
                { icon: '🔓', title: 'WILLINGNESS', desc: 'Have you actually done uncomfortable things?', color: '#22c55e' },
                { icon: '🔥', title: 'DESIRE', desc: 'Is your pull toward better strong enough?', color: '#f59e0b' },
                { icon: '💎', title: 'RESOURCES', desc: 'Do you have what\'s needed?', color: '#8b5cf6' },
                { icon: '⏰', title: 'TIMING', desc: 'Is now the right moment?', color: '#ec4899' },
                { icon: '🎯', title: 'BEHAVIOR PROOF', desc: 'Do your actions match your intentions?', color: '#06b6d4' },
              ].map((dim, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  padding: '16px',
                  background: 'rgba(255,255,255,0.02)',
                  borderRadius: '8px',
                  borderLeft: `3px solid ${dim.color}`
                }}>
                  <span style={{ fontSize: '24px' }}>{dim.icon}</span>
                  <div>
                    <div style={{ color: dim.color, fontWeight: '600', fontSize: '0.9rem' }}>{dim.title}</div>
                    <div style={{ color: '#94a3b8', fontSize: '0.9rem' }}>{dim.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Transformation Equation */}
          <div style={{
            background: 'rgba(139, 92, 246, 0.1)',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '32px',
            textAlign: 'center'
          }}>
            <div style={{ color: '#a78bfa', fontSize: '0.85rem', marginBottom: '8px', letterSpacing: '1px' }}>
              THE TRANSFORMATION EQUATION
            </div>
            <div style={{ 
              color: '#f8fafc', 
              fontSize: '1.3rem', 
              fontWeight: '600',
              fontFamily: 'monospace'
            }}>
              Shattered + Desire + Willingness &gt; Ego + Fear + Results
            </div>
            <div style={{ color: '#94a3b8', fontSize: '0.9rem', marginTop: '12px' }}>
              When the left side exceeds the right, transformation becomes possible.
            </div>
          </div>

          {/* Start Button */}
          <button
            onClick={() => {
              trackEvent('tra_start');
              setStarted(true);
              window.scrollTo(0, 0);
            }}
            style={{
              width: '100%',
              padding: '20px',
              fontSize: '1.1rem',
              fontWeight: '600',
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: '#1e293b',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              letterSpacing: '1px'
            }}
          >
            BEGIN ASSESSMENT →
          </button>

          <p style={{ 
            color: '#64748b', 
            fontSize: '0.85rem', 
            textAlign: 'center', 
            marginTop: '16px' 
          }}>
            {totalQuestions} questions • Takes 5-7 minutes • Be ruthlessly honest about past behavior
          </p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: RESULTS PAGE
  // ============================================================================

  if (showResults && calculateResults) {
    const results = calculateResults;
    
    const levelColors = {
      ready: '#22c55e',
      conditional: '#f59e0b',
      notReady: '#ef4444'
    };

    const levelIcons = {
      ready: '✅',
      conditional: '⚠️',
      notReady: '🚫'
    };

    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
        padding: '40px 20px',
        fontFamily: "'Space Grotesk', -apple-system, sans-serif"
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          {/* Main Result */}
          <div style={{
            background: `rgba(${results.readinessLevel === 'ready' ? '34, 197, 94' : results.readinessLevel === 'conditional' ? '245, 158, 11' : '239, 68, 68'}, 0.1)`,
            border: `2px solid ${levelColors[results.readinessLevel]}`,
            borderRadius: '16px',
            padding: '32px',
            textAlign: 'center',
            marginBottom: '32px'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>
              {levelIcons[results.readinessLevel]}
            </div>
            <h1 style={{
              color: levelColors[results.readinessLevel],
              fontSize: '2rem',
              fontWeight: '700',
              marginBottom: '8px'
            }}>
              {results.readinessMessage}
            </h1>
            <div style={{
              color: '#f8fafc',
              fontSize: '3rem',
              fontWeight: '700',
              marginBottom: '16px'
            }}>
              {results.overallPercentage}%
            </div>
            <p style={{
              color: '#94a3b8',
              fontSize: '1rem',
              maxWidth: '500px',
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              {results.recommendation}
            </p>
            <p style={{
              color: '#64748b',
              fontSize: '0.85rem',
              fontStyle: 'italic',
              marginTop: '16px',
              marginBottom: 0
            }}>
              Readiness is about conditions, not intelligence or potential. This is sequencing, not judgment.
            </p>
            
            {(results.capacityGated || results.proofGated) && (
              <div style={{
                marginTop: '16px',
                padding: '12px',
                background: 'rgba(239, 68, 68, 0.2)',
                borderRadius: '8px',
                fontSize: '0.9rem',
                color: '#fca5a5'
              }}>
                ⚠️ HARD GATE TRIGGERED: {results.capacityGated && 'CAPACITY < 50%'}{results.capacityGated && results.proofGated && ' + '}{results.proofGated && 'BEHAVIOR PROOF < 50%'} — This overrides the overall score.
              </div>
            )}
          </div>

          {/* FOR READY RESULTS: Show Advisory Engagement FIRST */}
          {results.readinessLevel === 'ready' && (
            <div style={{
              background: 'rgba(34, 197, 94, 0.1)',
              border: '1px solid rgba(34, 197, 94, 0.3)',
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '32px'
            }}>
              <h2 style={{ color: '#22c55e', fontSize: '1.2rem', marginBottom: '16px' }}>
                What This Means for Advisory Engagement
              </h2>
              
              <div style={{ color: '#94a3b8', lineHeight: '1.7' }}>
                {!results.isLikelyIdentityConstrained ? (
                  <>
                    <p><strong style={{ color: '#22c55e' }}>You're ready. Let's go.</strong></p>
                    <p style={{ marginBottom: 0 }}>You have the capacity, willingness, and follow-through to make real changes stick. We can move straight into diagnosing your business constraints and building your execution plan.</p>
                  </>
                ) : (
                  <>
                    <p><strong style={{ color: '#a78bfa' }}>You're ready—with some inner work to do along the way.</strong></p>
                    <p style={{ marginBottom: 0 }}>Your readiness scores are strong, and we can absolutely move forward. We'll just keep an eye on the identity-level patterns that showed up and address them as they arise during our work together.</p>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Dimension Breakdown / Readiness Profile */}
          <div style={{
            background: 'rgba(255,255,255,0.03)',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '32px'
          }}>
            <h2 style={{ color: '#f8fafc', fontSize: '1.2rem', marginBottom: '20px' }}>
              {results.readinessLevel === 'ready' ? 'Your Readiness Profile' : 'Dimension Breakdown'}
            </h2>
            
            {results.sectionScores.map((section, i) => (
              <div key={i} style={{ marginBottom: '16px' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  <span style={{ 
                    color: section.id === results.constraint.id 
                      ? (results.readinessLevel === 'ready' ? '#f59e0b' : '#ef4444')
                      : section.color, 
                    fontWeight: '600',
                    fontSize: '0.9rem'
                  }}>
                    {section.title} {section.id === results.constraint.id && (
                      results.readinessLevel === 'ready' 
                        ? '← AREA TO WATCH' 
                        : '← YOUR CONSTRAINT'
                    )}
                  </span>
                  <span style={{ 
                    color: section.percentage >= 75 ? '#22c55e' : section.percentage >= 50 ? '#f59e0b' : '#ef4444',
                    fontWeight: '600'
                  }}>
                    {section.percentage}%
                  </span>
                </div>
                <div style={{
                  height: '8px',
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%',
                    width: `${section.percentage}%`,
                    background: section.id === results.constraint.id 
                      ? (results.readinessLevel === 'ready' ? '#f59e0b' : '#ef4444')
                      : section.color,
                    borderRadius: '4px',
                    transition: 'width 0.5s ease'
                  }} />
                </div>
              </div>
            ))}
          </div>

          {/* Identity-Level Indicators */}
          {results.identityIndicators.length > 0 && (
            <div style={{
              background: 'rgba(139, 92, 246, 0.1)',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '32px'
            }}>
              <h3 style={{ color: '#a78bfa', fontSize: '1rem', marginBottom: '16px' }}>
                {results.readinessLevel === 'ready'
                  ? `🔮 Areas We'll Address Together (Score: ${results.identityScore.toFixed(1)}/5)`
                  : results.isLikelyIdentityConstrained 
                    ? `🔮 IDENTITY-LEVEL CONSTRAINT LIKELY (Score: ${results.identityScore.toFixed(1)}/5)` 
                    : `🔮 Potential Identity Factors (Score: ${results.identityScore.toFixed(1)}/5)`}
              </h3>
              <ul style={{ color: '#c4b5fd', margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
                {results.identityIndicators.map((indicator, i) => (
                  <li key={i}>{indicator}</li>
                ))}
              </ul>
              {results.readinessLevel === 'ready' ? (
                <p style={{ 
                  color: '#94a3b8', 
                  fontSize: '0.9rem', 
                  marginTop: '16px',
                  marginBottom: 0,
                  lineHeight: '1.6'
                }}>
                  These patterns are common and workable. During our engagement, we'll flag when these show up and help you move through them. Awareness is half the battle.
                </p>
              ) : results.isLikelyIdentityConstrained && (
                <p style={{ 
                  color: '#94a3b8', 
                  fontSize: '0.9rem', 
                  marginTop: '16px',
                  marginBottom: 0,
                  lineHeight: '1.6'
                }}>
                  Your constraint appears to be at the identity level, not tactical. More tactics, funnels, or strategies won't solve this. Consider working on the foundation: Receive → Contain → Give at the soul level.
                </p>
              )}
            </div>
          )}

          {/* Fit Flags - "Think Twice" indicators */}
          {results.fitFlags.length > 0 && (
            <div style={{
              background: results.hasRedFlags ? 'rgba(239, 68, 68, 0.1)' : 'rgba(245, 158, 11, 0.1)',
              border: `1px solid ${results.hasRedFlags ? 'rgba(239, 68, 68, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`,
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '32px'
            }}>
              <h3 style={{ 
                color: results.hasRedFlags ? '#ef4444' : '#f59e0b', 
                fontSize: '1rem', 
                marginBottom: '16px' 
              }}>
                {results.hasRedFlags ? '⚠️ Engagement Fit Concerns' : '⚡ Items to Discuss'}
              </h3>
              <ul style={{ 
                color: results.hasRedFlags ? '#fca5a5' : '#fcd34d', 
                margin: 0, 
                paddingLeft: '20px', 
                lineHeight: '1.8' 
              }}>
                {results.fitFlags.map((flag, i) => (
                  <li key={i} style={{ 
                    color: flag.severity === 'red' ? '#fca5a5' : '#fcd34d' 
                  }}>
                    {flag.message}
                  </li>
                ))}
              </ul>
              <p style={{ 
                color: '#94a3b8', 
                fontSize: '0.9rem', 
                marginTop: '16px',
                marginBottom: 0,
                lineHeight: '1.6'
              }}>
                {results.hasRedFlags 
                  ? 'These patterns often derail engagements. If we move forward, we\'ll need to address these directly in our first conversation.'
                  : 'These came up in your assessment. We\'ll want to discuss them to ensure we\'re set up for success.'}
              </p>
            </div>
          )}

          {/* Areas to Strengthen (only for NOT ready results) */}
          {results.readinessLevel !== 'ready' && (
            <div style={{
              background: 'rgba(255,255,255,0.03)',
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '32px'
            }}>
              <h2 style={{ color: '#f8fafc', fontSize: '1.2rem', marginBottom: '16px' }}>
                Areas to Strengthen
              </h2>
              
              {results.recommendedTrack === 'identity' && (
                <div style={{
                  padding: '20px',
                  background: 'rgba(139, 92, 246, 0.1)',
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                  borderRadius: '12px'
                }}>
                  <div style={{ color: '#a78bfa', fontWeight: '700', fontSize: '1.1rem', marginBottom: '8px' }}>
                    🔮 IDENTITY/FOUNDATION WORK
                  </div>
                  <p style={{ color: '#94a3b8', margin: 0, lineHeight: '1.6' }}>
                    Before tactical optimization, address the deeper constraint. The Jewish Wealth Cycle framework (Receive → Contain → Give) works at the soul level where your real block lives. Tactics applied on top of identity constraints don't stick.
                  </p>
                  <p style={{ color: '#a78bfa', margin: '12px 0 0 0', fontSize: '0.9rem' }}>
                    <strong>Next step:</strong> Run the Blessing Block Calculator to identify your primary vessel constraint, then begin there.
                  </p>
                </div>
              )}
              
              {results.recommendedTrack === 'foundation' && (
                <div style={{
                  padding: '20px',
                  background: 'rgba(245, 158, 11, 0.1)',
                  border: '1px solid rgba(245, 158, 11, 0.3)',
                  borderRadius: '12px'
                }}>
                  <div style={{ color: '#f59e0b', fontWeight: '700', fontSize: '1.1rem', marginBottom: '8px' }}>
                    🏗️ STABILIZATION FIRST
                  </div>
                  <p style={{ color: '#94a3b8', margin: 0, lineHeight: '1.6' }}>
                    The conditions for transformation aren't quite right yet. Focus on building the missing dimension ({results.constraint.title}) before attempting strategic overhaul. This isn't failure—it's intelligent sequencing. When the equation tips, you'll know.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* What This Means for Advisory Engagement (only for NOT ready results) */}
          {results.readinessLevel !== 'ready' && (
            <div style={{
              background: 'rgba(255,255,255,0.03)',
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '32px'
            }}>
              <h2 style={{ color: '#f8fafc', fontSize: '1.2rem', marginBottom: '16px' }}>
                What This Means for Advisory Engagement
              </h2>
              
              <div style={{ color: '#94a3b8', lineHeight: '1.7' }}>
                {results.readinessLevel === 'conditional' && (
                  <>
                    <p><strong style={{ color: '#f59e0b' }}>Scoped engagement recommended.</strong></p>
                    <p style={{ marginBottom: 0 }}>A full transformation engagement would likely stall. Better approach: a focused engagement specifically designed to build your weak dimension ({results.constraint.title}), then reassess for broader work.</p>
                  </>
                )}
                
                {results.readinessLevel === 'notReady' && (
                  <>
                    <p><strong style={{ color: '#ef4444' }}>Not the right time for advisory engagement.</strong></p>
                    <p style={{ marginBottom: 0 }}>Taking on a significant engagement now would likely waste your money and our time. Better path: stabilize, build foundation, revisit in 3-6 months. Consider lower-investment resources (book, course, peer group) in the meantime.</p>
                  </>
                )}
              </div>
            </div>
          )}

          {/* EMAIL CAPTURE + CTA SECTION */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            borderRadius: '16px',
            padding: '32px',
            marginBottom: '32px'
          }}>
            {/* Email Input */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ color: '#f8fafc', fontSize: '1.1rem', marginBottom: '8px' }}>
                📧 Send Your Results
              </h3>
              <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginBottom: '16px' }}>
                Email your results to Adam for a personalized follow-up.
              </p>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com (so I can reply)"
                  style={{
                    flex: '1',
                    minWidth: '200px',
                    padding: '14px 16px',
                    fontSize: '1rem',
                    background: 'rgba(15, 23, 42, 0.8)',
                    border: '1px solid #475569',
                    borderRadius: '8px',
                    color: '#f8fafc',
                    outline: 'none'
                  }}
                />
                <button
                  onClick={handleEmailSubmit}
                  disabled={!email || emailSubmitted}
                  style={{
                    padding: '14px 24px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    background: emailSubmitted ? '#22c55e' : (email ? '#f59e0b' : '#475569'),
                    color: emailSubmitted ? '#fff' : '#1e293b',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: email && !emailSubmitted ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s ease',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {emailSubmitted ? '✓ Opened!' : 'Email Results'}
                </button>
              </div>
              {emailSubmitted && (
                <p style={{ color: '#22c55e', fontSize: '0.85rem', marginTop: '8px', marginBottom: 0 }}>
                  Your email app should have opened. Just hit send and I'll follow up personally.
                </p>
              )}
            </div>

            {/* Divider */}
            <div style={{ 
              height: '1px', 
              background: 'rgba(255,255,255,0.1)', 
              margin: '24px 0' 
            }} />

            {/* CTA Based on Result */}
            <div>
              <h3 style={{ color: '#f8fafc', fontSize: '1.1rem', marginBottom: '16px' }}>
                Your Next Step
              </h3>
              
              {/* READY: Schedule Strategy Call */}
              {results.readinessLevel === 'ready' && !results.isLikelyIdentityConstrained && (
                <div>
                  <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginBottom: '16px' }}>
                    Let's identify your specific business constraint and map out your 90-day execution plan.
                  </p>
                  <button
                    onClick={() => handleCtaClick('strategy_call', buildCalendlyUrl('https://square.site/book/D8274NM6KD1X2/libman-tax-strategies-llc-arcadia-ca'))}
                    style={{
                      width: '100%',
                      padding: '18px',
                      fontSize: '1.1rem',
                      fontWeight: '700',
                      background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      letterSpacing: '0.5px'
                    }}
                  >
                    SCHEDULE STRATEGY CALL →
                  </button>
                </div>
              )}

              {/* READY + IDENTITY: Schedule Call (Different framing) */}
              {results.readinessLevel === 'ready' && results.isLikelyIdentityConstrained && (
                <div>
                  <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginBottom: '16px' }}>
                    Let's talk about your business goals and how we'll work through the identity patterns together as they come up.
                  </p>
                  <button
                    onClick={() => handleCtaClick('strategy_call_identity', buildCalendlyUrl('https://square.site/book/D8274NM6KD1X2/libman-tax-strategies-llc-arcadia-ca'))}
                    style={{
                      width: '100%',
                      padding: '18px',
                      fontSize: '1.1rem',
                      fontWeight: '700',
                      background: 'linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      letterSpacing: '0.5px'
                    }}
                  >
                    SCHEDULE STRATEGY CALL →
                  </button>
                </div>
              )}

              {/* CONDITIONAL: Run Diagnostic or Schedule Scoped Call */}
              {results.readinessLevel === 'conditional' && (
                <div>
                  <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginBottom: '16px' }}>
                    Before a full engagement, let's identify exactly where you're stuck. Run our business diagnostic to pinpoint your constraint.
                  </p>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => handleCtaClick('diagnostic', 'https://mozi6-diagnostic.vercel.app/')}
                      style={{
                        flex: '1',
                        minWidth: '200px',
                        padding: '16px',
                        fontSize: '1rem',
                        fontWeight: '600',
                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                        color: '#1e293b',
                        border: 'none',
                        borderRadius: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      FIND YOUR CONSTRAINT →
                    </button>
                    <button
                      onClick={() => handleCtaClick('discovery_call', buildCalendlyUrl('https://square.site/book/D8274NM6KD1X2/libman-tax-strategies-llc-arcadia-ca'))}
                      style={{
                        flex: '1',
                        minWidth: '200px',
                        padding: '16px',
                        fontSize: '1rem',
                        fontWeight: '600',
                        background: 'transparent',
                        color: '#f59e0b',
                        border: '2px solid #f59e0b',
                        borderRadius: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      BOOK 15-MIN CALL
                    </button>
                  </div>
                </div>
              )}

              {/* NOT READY: Get the Book / Join Waitlist */}
              {results.readinessLevel === 'notReady' && (
                <div>
                  <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginBottom: '16px' }}>
                    Now isn't the right time for a major engagement—and that's okay. Build your foundation first with these resources:
                  </p>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => handleCtaClick('book', 'https://jewishwealthcycle.com/')}
                      style={{
                        flex: '1',
                        minWidth: '200px',
                        padding: '16px',
                        fontSize: '1rem',
                        fontWeight: '600',
                        background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      GET THE BOOK →
                    </button>
                    <button
                      onClick={() => setShowWaitlist(true)}
                      style={{
                        flex: '1',
                        minWidth: '200px',
                        padding: '16px',
                        fontSize: '1rem',
                        fontWeight: '600',
                        background: 'transparent',
                        color: '#94a3b8',
                        border: '2px solid #475569',
                        borderRadius: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      NOTIFY ME WHEN READY
                    </button>
                  </div>
                  <p style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '12px', marginBottom: 0 }}>
                    We'll check in with you in 90 days to see if conditions have changed.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Reset Button */}
          <button
            onClick={resetAssessment}
            style={{
              width: '100%',
              padding: '16px',
              fontSize: '1rem',
              fontWeight: '600',
              background: 'transparent',
              color: '#94a3b8',
              border: '1px solid #475569',
              borderRadius: '12px',
              cursor: 'pointer'
            }}
          >
            RETAKE ASSESSMENT
          </button>

          {/* Attribution */}
          <div style={{
            textAlign: 'center',
            marginTop: '48px',
            color: '#64748b',
            fontSize: '0.85rem'
          }}>
            <em>The Jewish Wealth Cycle</em> by Adam C. Libman CRTP
            <br />
            <span style={{ fontSize: '0.75rem' }}>Libman Tax Strategies LLC • 25 Years Serving Entrepreneurs</span>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: QUESTIONS PAGE
  // ============================================================================

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
      padding: '40px 20px',
      fontFamily: "'Space Grotesk', -apple-system, sans-serif"
    }}>
      <div style={{ maxWidth: '700px', margin: '0 auto' }}>
        {/* Progress */}
        <div style={{ marginBottom: '32px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
              Section {currentSection + 1} of {sections.length}
            </span>
            <span style={{ color: currentSectionData.color, fontWeight: '600' }}>
              {currentSectionData.icon} {currentSectionData.title}
            </span>
          </div>
          <div style={{
            height: '4px',
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            <div style={{
              height: '100%',
              width: `${((currentSection + 1) / sections.length) * 100}%`,
              background: currentSectionData.color,
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>

        {/* Section Header */}
        <div style={{ 
          textAlign: 'center', 
          marginBottom: '32px',
          padding: '24px',
          background: `rgba(${currentSectionData.color === '#3b82f6' ? '59, 130, 246' : 
                           currentSectionData.color === '#22c55e' ? '34, 197, 94' :
                           currentSectionData.color === '#f59e0b' ? '245, 158, 11' :
                           currentSectionData.color === '#8b5cf6' ? '139, 92, 246' :
                           currentSectionData.color === '#ec4899' ? '236, 72, 153' : '6, 182, 212'}, 0.1)`,
          borderRadius: '12px',
          borderLeft: `4px solid ${currentSectionData.color}`
        }}>
          <div style={{ fontSize: '40px', marginBottom: '8px' }}>{currentSectionData.icon}</div>
          <h2 style={{ color: currentSectionData.color, fontSize: '1.5rem', marginBottom: '8px' }}>
            {currentSectionData.title}
          </h2>
          <p style={{ color: '#94a3b8', margin: 0 }}>{currentSectionData.subtitle}</p>
        </div>

        {/* Questions */}
        <div style={{ marginBottom: '32px' }}>
          {currentQuestions.map((question, i) => (
            <div key={question.id} style={{
              background: 'rgba(255,255,255,0.03)',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '16px',
              border: answers[question.id] !== undefined ? `1px solid ${currentSectionData.color}` : '1px solid transparent'
            }}>
              <p style={{ 
                color: '#f8fafc', 
                fontSize: '1rem', 
                marginBottom: '8px',
                lineHeight: '1.5'
              }}>
                {i + 1}. {question.text}
              </p>
              <p style={{ 
                color: '#64748b', 
                fontSize: '0.85rem', 
                marginBottom: '16px',
                fontStyle: 'italic'
              }}>
                {question.subtext}
              </p>
              
              {/* Custom options for somatic questions */}
              {question.options ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {question.options.map(option => (
                    <button
                      key={option.value}
                      onClick={() => handleAnswer(question.id, option.value)}
                      style={{
                        padding: '14px 16px',
                        fontSize: '0.9rem',
                        fontWeight: '500',
                        textAlign: 'left',
                        background: answers[question.id] === option.value 
                          ? (option.value === 2 ? '#22c55e' : option.value === 1 ? '#f59e0b' : '#ef4444')
                          : 'rgba(255,255,255,0.05)',
                        color: answers[question.id] === option.value ? '#1e293b' : '#94a3b8',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        lineHeight: '1.4'
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              ) : (
                /* Standard Yes/Somewhat/No options */
                <div style={{ display: 'flex', gap: '12px' }}>
                  {[
                    { value: 0, label: 'No', color: '#ef4444' },
                    { value: 1, label: 'Somewhat', color: '#f59e0b' },
                    { value: 2, label: 'Yes', color: '#22c55e' },
                  ].map(option => (
                    <button
                      key={option.value}
                      onClick={() => handleAnswer(question.id, option.value)}
                      style={{
                        flex: 1,
                        padding: '12px',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        background: answers[question.id] === option.value 
                          ? option.color 
                          : 'rgba(255,255,255,0.05)',
                        color: answers[question.id] === option.value ? '#1e293b' : '#94a3b8',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Navigation */}
        <div style={{ display: 'flex', gap: '16px' }}>
          <button
            onClick={handleBack}
            disabled={currentSection === 0}
            style={{
              flex: 1,
              padding: '16px',
              fontSize: '1rem',
              fontWeight: '600',
              background: 'transparent',
              color: currentSection === 0 ? '#475569' : '#94a3b8',
              border: '1px solid #475569',
              borderRadius: '12px',
              cursor: currentSection === 0 ? 'not-allowed' : 'pointer'
            }}
          >
            ← Back
          </button>
          <button
            onClick={handleNext}
            disabled={!allCurrentAnswered}
            style={{
              flex: 2,
              padding: '16px',
              fontSize: '1rem',
              fontWeight: '600',
              background: allCurrentAnswered 
                ? `linear-gradient(135deg, ${currentSectionData.color} 0%, ${currentSectionData.color}cc 100%)`
                : '#475569',
              color: allCurrentAnswered ? '#1e293b' : '#64748b',
              border: 'none',
              borderRadius: '12px',
              cursor: allCurrentAnswered ? 'pointer' : 'not-allowed'
            }}
          >
            {currentSection === sections.length - 1 ? 'See Results →' : 'Continue →'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransformationReadinessAssessment;
